import { useEffect, useState, useRef, isValidElement } from 'react';
import { C, E, M, R, Z } from '../design.js';
import { Icon } from './UI.js';

// ══════════════════════════════════════════════
//  TOAST — Sistema de notificaciones efímeras
//  API imperativa (sin Provider): toast.success(msg)
//  Reemplaza alert() en toda la app.
// ══════════════════════════════════════════════

let _id = 0;
const _listeners = new Set();
const _emit = (toast) => _listeners.forEach(fn => fn(toast));

const push = (kind, message, opts = {}) => {
  const id = ++_id;
  const t = {
    id,
    kind,                            // 'success' | 'error' | 'warning' | 'info' | 'loading'
    // Acepta JSX (p.ej. un icono inline a escala de texto); todo lo demás
    // se coerciona a string como siempre (errores, números, null).
    message: isValidElement(message) ? message : String(message ?? ''),
    // 'loading' es persistente (duration 0): vive hasta que se actualice/cierre.
    duration: opts.duration ?? (kind === 'error' ? 6000 : kind === 'loading' ? 0 : 3500),
    title: opts.title,
    progress: opts.progress,         // 0..1 → barra de progreso; undefined → sin barra
    action: opts.action,             // { label, onClick } → botón dentro del toast
  };
  _emit({ type: 'add', toast: t });
  return id;
};

const dismiss = (id) => _emit({ type: 'remove', id });
// Actualiza un toast existente en sitio (mensaje, progreso, kind, duración, acción).
// Pensado para procesos largos: toast.loading() → toast.update(id, {progress}) → success/error.
const update = (id, patch) => _emit({ type: 'update', id, patch: patch || {} });

export const toast = {
  success: (msg, opts) => push('success', msg, opts),
  error:   (msg, opts) => push('error',   msg, opts),
  warning: (msg, opts) => push('warning', msg, opts),
  info:    (msg, opts) => push('info',    msg, opts),
  loading: (msg, opts) => push('loading', msg, opts),
  update,
  dismiss,
};

// ── Estilos por tipo ──────────────────────────
// El tono vive SOLO en la píldora del icono (patrón de ConfirmModal /
// PDFConfirmModal): tarjeta neutra, texto neutro — informa sin regañar.
const STYLES = {
  success: { bg: C.successLight,  fg: C.success,  icon: "check-circle" },
  error:   { bg: C.criticalLight, fg: C.critical, icon: "alert-circle" },
  warning: { bg: C.warningLight,  fg: C.warning,  icon: "alert-triangle" },
  info:    { bg: C.primaryLight,  fg: C.primary,  icon: "info" },
  loading: { bg: C.primaryLight,  fg: C.primary,  icon: "info" },
};

// ── Toaster — montar UNA sola vez en App ──────
export function Toaster() {
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    const scheduleDismiss = (id, duration) => {
      const old = timersRef.current.get(id);
      if (old) { clearTimeout(old); timersRef.current.delete(id); }
      if (duration > 0) {
        const tid = setTimeout(() => {
          setItems(prev => prev.filter(t => t.id !== id));
          timersRef.current.delete(id);
        }, duration);
        timersRef.current.set(id, tid);
      }
    };
    const onEvent = (e) => {
      if (e.type === 'add') {
        setItems(prev => [...prev, e.toast]);
        // duration 0/null → toast persistente (ej. 'loading'): no auto-cierra.
        scheduleDismiss(e.toast.id, e.toast.duration);
      } else if (e.type === 'update') {
        setItems(prev => prev.map(t => t.id === e.id ? { ...t, ...e.patch } : t));
        if (e.patch && 'duration' in e.patch) scheduleDismiss(e.id, e.patch.duration);
      } else if (e.type === 'remove') {
        setItems(prev => prev.filter(t => t.id !== e.id));
        const tid = timersRef.current.get(e.id);
        if (tid) { clearTimeout(tid); timersRef.current.delete(e.id); }
      }
    };
    _listeners.add(onEvent);
    // El Map del ref se crea una sola vez y nunca se reasigna: copiarlo a una
    // const dentro del efecto deja el cleanup idéntico y sin leer ref.current.
    const timers = timersRef.current;
    return () => {
      _listeners.delete(onEvent);
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Notificaciones"
      data-gb-toaster=""
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: Z.toast,
        display: "flex",
        flexDirection: "column-reverse",
        gap: 10,
        maxWidth: "calc(100vw - 40px)",
        width: 380,
        pointerEvents: "none",
      }}
    >
      <style>{`
        @keyframes toast-in { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      {items.map(t => {
        const s = STYLES[t.kind] || STYLES.info;
        const isLoading = t.kind === 'loading';
        const hasProgress = typeof t.progress === 'number';
        const multiLinea = !!(t.title || t.action || hasProgress);
        return (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            style={{
              pointerEvents: "auto",
              // Tarjeta neutra OPACA (legibilidad primero); el tono del
              // estado vive solo en la píldora del icono — sin franjas
              // ni texto teñido.
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: R.lg,
              padding: "10px 12px",
              boxShadow: E.e3,
              display: "flex",
              flexDirection: "column",
              gap: hasProgress ? 8 : 0,
              animation: `toast-in ${M.base} ${M.easeOut}`,
              color: C.text,
              fontFamily: "inherit",
            }}
          >
            <div style={{ display: "flex", alignItems: multiLinea ? "flex-start" : "center", gap: 10 }}>
              <div style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: R.sm,
                background: s.bg, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isLoading ? (
                  <span className="gb-spin" style={{
                    display: "block", width: 13, height: 13,
                    border: `2px solid ${C.surface}`, borderTopColor: s.fg,
                  }} />
                ) : (
                  <Icon name={s.icon} size={15} color={s.fg} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {t.title && <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 2 }}>{t.title}</div>}
                <div style={{ fontSize: 12.5, lineHeight: 1.45, color: t.title ? C.textMed : C.text, wordBreak: "break-word" }}>
                  {t.message}
                </div>
                {t.action && (
                  <button
                    onClick={() => t.action.onClick && t.action.onClick()}
                    className="gb-press gb-i gb-focus"
                    style={{
                      marginTop: 8, background: s.bg, color: s.fg, border: "none",
                      borderRadius: R.ctl, padding: "5px 12px", fontSize: 12, fontWeight: 700,
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {t.action.label}
                  </button>
                )}
              </div>
              {/* Un 'loading' no se puede cerrar a mano: protege el progreso y la acción de reintento */}
              {!isLoading && (
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Cerrar notificación"
                  className="gb-press gb-i gb-focus"
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: C.textMuted,
                    padding: 3,
                    flexShrink: 0,
                    lineHeight: 0,
                  }}
                >
                  <Icon name="x" size={13} color="currentColor" />
                </button>
              )}
            </div>
            {hasProgress && (
              <div style={{ height: 4, borderRadius: 3, background: C.surfaceSunken, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${Math.max(0, Math.min(1, t.progress)) * 100}%`,
                  background: s.fg, borderRadius: 3, transition: `width ${M.slow} ${M.ease}`,
                }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
